import { inject, injectable, multiInject } from '@patsnap/tahiti-core/shared/inversify';
import { type Event, Emitter } from '@patsnap/tahiti-core/common/event';
import type { SpecificationGenContentService } from '../specification-gen-content-service';
import type { SpecificationContent, SpecificationGenContentState, SpecificationParagraph, SpecificationParagraphWithData } from '../../types/specification-gen-content';
import { SpecificationGenContentModel } from '../../models/specification-gen-content-model';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import type { SpecificationStreamContent } from '../../types/specification';
import { RootSection } from '../sections/root-section';
import _ from 'lodash';
import { SpecificationSection } from '../specification-section';

@injectable()
export class SpecificationGenContentServiceImpl implements SpecificationGenContentService {
    private _onContentGeneratingEmitter = new Emitter<SpecificationStreamContent<SpecificationContent>>();
    readonly onContentGenerating = this._onContentGeneratingEmitter.event;

    constructor(
        @inject(SpecificationGenContentModel)
        private _model: SpecificationGenContentModel,
        @inject(RootSection)
        private _rootSection: RootSection,
        @multiInject(SpecificationSection)
        private _sections: SpecificationSection[],
    ) {}

    get onStateChange(): Event<SpecificationGenContentState> {
        return this._model.onStatesChange;
    }

    getState(): SpecificationGenContentState {
        return this._model.modelData;
    }

    updateState(state: SpecificationGenContentState) {
        this._model.update(() => state);
    }

    async init(taskId: string): Promise<void> {
        // 如果 taskId 没有变化，不需要重新初始化
        if (this.getState().taskId === taskId) {
            return;
        }

        this._model.update(state => ({
            ...state,
            taskId,
        }));

        await this._rootSection
            .execute()
            .subscribe(this._onGenerateEvent.bind(this))
            .promise;
    }

    async regenerate(): Promise<void> {
        const { taskId } = this.getState();
        if (!taskId) {
            return;
        }

        // 先取消当前的生成任务
        this.stop();

        // 重新生成
        await this._rootSection
            .execute(true)
            .subscribe(this._onGenerateEvent.bind(this))
            .promise;
    }

    _onGenerateEvent(data: SpecificationParagraph) {
        const generatedContent: SpecificationStreamContent<SpecificationContent> = {
            status: data.status,
            content: data.children || [],
        };
        this._model.update(state => ({
            ...state,
            generatedContent,
        }));

        if (data.status === StreamStatus.PROCESSING) {
            this._onContentGeneratingEmitter.fire(generatedContent);
        }
    }

    stop(): void {
        this._rootSection && this._rootSection.stop();
    }

    async updateParagraphContent(paragraph: SpecificationParagraphWithData): Promise<void> {
        const { generatedContent } = _.cloneDeep(this.getState());
        if (paragraph) {
            this._model.update(state => ({
                ...state,
                generatedContent: {
                    ...state.generatedContent,
                    content: generatedContent.content,
                },
            }));

            const sectionService = this._sections.find(section => section.contentType === paragraph.type);
            if (typeof sectionService?.save === 'function') {
                await sectionService.save(paragraph);
            }
        }
    }

    async deleteParagraphContent(paragraph: SpecificationParagraphWithData): Promise<void> {
        const { generatedContent } = _.cloneDeep(this.getState());
        this._model.update(state => ({
            ...state,
            generatedContent: {
                ...state.generatedContent,
                content: generatedContent.content,
            },
        }));
        if (paragraph) {
            const sectionService = this._sections.find(section => section.contentType === paragraph.type);
            if (typeof sectionService?.remove === 'function') {
                await sectionService.remove(paragraph);
            }
        }
    }
}
